import API from 'shared/api/API';
import { POST, GET, PATCH } from 'shared/utils/Executer';
import axios from 'axios';

const AUTO_UPLOADS_SERVICE_PATH = '/v1/auto-uploads';
const CONTENT_TYPE = 'application/zip';
// Mirrors AUTO_UPLOAD_MAPPING_MODES on the backend; the public /mapping endpoint
// only accepts ZIP_STRUCTURE ('Map' mode is internal-only for now)
const UPLOAD_MODE = {
  MAP: 'Map',
  ZIP_STRUCTURE: 'ZipStructure',
};

class AutoUploadsAPI {
  /**
   * Create a new auto upload session and get signed URL for direct upload
   * @param {Object} params
   * @param {string} params.workspaceId - Workspace ID
   * @param {string} params.fileName - Name of the file to upload
   * @param {number} params.fileSize - Size of the file in bytes
   * @param {string} [params.contentType='application/zip'] - MIME type of the file
   * @param {string} [params.parentCategoryId] - Optional parent category ID
   * @returns {Promise<{uploadId: string, signedUrl: string, expiresAt: string}>} Upload session with signed URL
   */
  async createUpload({
    workspaceId,
    fileName,
    fileSize,
    contentType = CONTENT_TYPE,
    parentCategoryId,
  }) {
    return await API.client.execute(POST, AUTO_UPLOADS_SERVICE_PATH, {
      workspaceId,
      fileName,
      fileSize,
      contentType,
      parentCategoryId,
    });
  }

  /**
   * Upload file directly to GCS using signed URL
   * @param {string} signedUrl - Pre-signed GCS URL
   * @param {File} file - File to upload
   * @param {Function} [onProgress] - Progress callback (progressEvent) => void
   * @returns {Promise<void>}
   */
  async uploadToGCS(signedUrl, file, onProgress) {
    return await axios.put(signedUrl, file, {
      headers: {
        'Content-Type': CONTENT_TYPE,
      },
      onUploadProgress: onProgress,
    });
  }

  /**
   * Get upload by ID
   * @param {string} uploadId - Upload ID
   * @returns {Promise<Object>} Upload details
   */
  async getUploadById(uploadId) {
    return await API.client.execute(GET, `${AUTO_UPLOADS_SERVICE_PATH}/${uploadId}`);
  }

  /**
   * Get all uploads for the current user's organization
   * @param {Object} params
   * @param {string} [params.workspaceId] - Optional workspace filter
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {string} [params.status] - Optional status filter
   * @param {string} [params.createdBy] - Optional creator filter
   * @returns {Promise<Object>} Paginated uploads list
   */
  async getAllUploads({ workspaceId, page = 1, limit = 20, status, createdBy } = {}) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (workspaceId) params.append('workspaceId', workspaceId);
    if (status) params.append('status', status);
    if (createdBy) params.append('createdBy', createdBy);

    return await API.client.execute(GET, `${AUTO_UPLOADS_SERVICE_PATH}?${params.toString()}`);
  }

  /**
   * Get files for a specific upload
   * @param {string} uploadId - Upload ID
   * @param {Object} [options] - Query options
   * @param {number} [options.page=1] - Page number
   * @param {number} [options.limit=50] - Items per page
   * @param {string} [options.status] - Filter by status
   * @returns {Promise<Object>} Upload files with pagination
   */
  async getUploadFiles(uploadId, options = {}) {
    const { page = 1, limit = 50, status } = options;
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (status) params.append('status', status);

    return await API.client.execute(
      GET,
      `${AUTO_UPLOADS_SERVICE_PATH}/${uploadId}/files?${params.toString()}`,
    );
  }

  /**
   * Build the upload's C8 structure from the ZIP folder hierarchy (must be called
   * before startUploadProcessing)
   * @param {string} uploadId - Upload ID
   * @param {Object} [options]
   * @param {string} [options.parentCategoryId] - Optional parent category override
   * @returns {Promise<{uploadId: string, status: string, filesMapped: number, categoriesDeleted: number, skippedFiles: number}>} Mapping result
   */
  async applyMapping(uploadId, { parentCategoryId } = {}) {
    return await API.client.execute(POST, `${AUTO_UPLOADS_SERVICE_PATH}/${uploadId}/mapping`, {
      mode: UPLOAD_MODE.ZIP_STRUCTURE,
      ...(parentCategoryId && { parentCategoryId }),
    });
  }

  /**
   * Start processing a mapped upload (triggers ZIP extraction and file processing)
   * @param {string} uploadId - Upload ID
   * @param {Object} [options]
   * @param {boolean} [options.skipMetadataExtraction] - Skip AI metadata extraction
   * @returns {Promise<Object>} Processing status
   */
  async startUploadProcessing(uploadId, { skipMetadataExtraction } = {}) {
    const params = new URLSearchParams();
    if (skipMetadataExtraction) {
      params.append('skipMetadataExtraction', 'true');
    }

    return await API.client.execute(
      POST,
      `${AUTO_UPLOADS_SERVICE_PATH}/${uploadId}/start?${params.toString()}`,
    );
  }

  /**
   * Enable metadata extraction for an upload that previously had it skipped
   * @param {string} uploadId - Upload ID
   * @returns {Promise<Object>} Success response
   */
  async enableMetadataExtraction(uploadId) {
    return await API.client.execute(
      PATCH,
      `${AUTO_UPLOADS_SERVICE_PATH}/${uploadId}/enable-metadata-extraction`,
    );
  }

  /**
   * Queue metadata extraction for knowledge items
   * @param {Object} params
   * @param {string[]} [params.knowledgeItemIds] - Array of knowledge item IDs
   * @param {boolean} [params.extractAll] - If true, extract metadata for all items in the organization
   * @param {string[]} [params.fields] - Optional array of fields to extract
   * @returns {Promise<Object>} Response with tasksCreated, requestedItems, and nonExistentIds
   */
  async queueMetadataExtraction({ knowledgeItemIds = [], extractAll = false, fields }) {
    const body = {
      knowledgeItemIds,
      extractAll,
    };

    if (fields && fields.length > 0) {
      body.fields = fields;
    }

    return await API.client.execute(POST, '/v1/internal/knowledge/queue-metadata-extraction', body);
  }
}

export default new AutoUploadsAPI();
